'use client';

import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import type { Driver, DriverAvailability } from '@/features/drivers';

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-teal-500'
];

function getAvatarColor(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const STATUS_DOT_STYLES: Record<DriverAvailability, string> = {
  available: 'bg-green-500',
  busy: 'bg-orange-500',
  on_break: 'bg-gray-400'
};

interface DriverCardProps {
  driver: Driver;
  selected: boolean;
  onSelect: (id: number) => void;
}

export function DriverCard({ driver, selected, onSelect }: DriverCardProps) {
  const t = useTranslations('shipping');

  const availability = driver.driver_profile?.status ?? 'busy';
  const isAvailable = availability === 'available';

  const statusLabels: Record<DriverAvailability, string> = {
    available: t('driverAvailable'),
    busy: t('driverBusy'),
    on_break: t('driverOnBreak')
  };

  return (
    <Card
      role='button'
      tabIndex={isAvailable ? 0 : -1}
      aria-disabled={!isAvailable}
      aria-selected={selected}
      onClick={() => isAvailable && onSelect(driver.id)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && isAvailable) {
          e.preventDefault();
          onSelect(driver.id);
        }
      }}
      className={cn(
        'relative transition-all',
        isAvailable
          ? 'cursor-pointer hover:shadow-md'
          : 'cursor-not-allowed opacity-50',
        selected && 'ring-primary ring-2'
      )}
    >
      {selected && (
        <div className='bg-primary text-primary-foreground absolute -end-2 -top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full'>
          <Check className='h-3.5 w-3.5' />
        </div>
      )}
      <CardContent className='flex items-center gap-3 p-4'>
        {/* Avatar */}
        <div
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white',
            getAvatarColor(driver.id)
          )}
        >
          {getInitials(driver.name)}
        </div>

        {/* Info */}
        <div className='min-w-0 flex-1'>
          <p className='truncate font-bold'>{driver.name}</p>

          <div className='mt-1.5 flex items-center gap-3'>
            <div className='flex items-center gap-1.5'>
              <span
                className={cn(
                  'inline-block h-2 w-2 rounded-full',
                  STATUS_DOT_STYLES[availability]
                )}
              />
              <span className='text-muted-foreground text-xs'>
                {statusLabels[availability]}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
